/* eslint-disable react/jsx-curly-brace-presence */
import { ExclamationOutlined, MoreOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@apollo/client";
import { Dropdown, Empty, message, Modal, Skeleton, Tooltip } from "antd";
import { capitalize } from "lodash";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DELETE_LEAD_TASK, UPDATE_LEAD_TASK } from "../../../graphql/mutation";
import { GET_LEAD_TASKS } from "../../../graphql/query";
import { useGetOpportunityId } from "../../../hooks/crmIntegrationHook";
import { convertUtcToLocal } from "../../../library/utils";
import { deleteTask, reopenTask, setTasks } from "../../../redux/slices/taskSlice";
import { snakeCaseToTitleCase } from "../../../utils/cases";
import { checkAccessPermission } from "../../../library/helpers";
import { AccessPermissionAction } from "../../../library/constants";
import useCrm from "../../../hooks/useCrm";
import { getRootOppTypeId } from "../../../library/oppTypeScoping";
import { KNOWN_ROOT_IDS, getRootConfig } from "../../../library/oppTypeRootConfig";
import { formatDate } from "../../../utils/dateTimes";

// Top-right opp-type chip — mirrors the opportunity / appointment /
// note cards. Returns null when the task isn't tied to an opp with a
// known root.
const TaskRootChip = ({ opportunityType }) => {
  const rootId = getRootOppTypeId(opportunityType);
  if (!rootId || !KNOWN_ROOT_IDS.has(Number(rootId))) return null;
  const { Icon, stroke, chipBg } = getRootConfig(rootId);
  return (
    <span
      className="absolute right-0 top-0 flex items-center justify-center"
      style={{ width: 20, height: 20, background: chipBg, borderBottomLeftRadius: 4 }}
    >
      <Icon size={13} color={stroke} strokeWidth={1.33} />
    </span>
  );
};

export const LeadTaskList = ({
  selectedLead,
  setleadTaskData,
  // Opp-type-scoped tab — see ``LeadProfile`` / ``engagementActivity``
  // for derivation. ``null`` ⇒ unfiltered.
  selectedOppTypeId,
}) => {
  const dispatch = useDispatch();
  const { tasks } = useSelector(state => state.tasks);
  const taskStatus = [
    { value: "ALL", label: "All" },
    { value: "CREATED", label: "Upcoming" },
    { value: "COMPLETED", label: "Completed" },
    { value: "OVERDUE", label: "Overdue" },
  ];
  const [selectedStatus, setselectedStatus] = useState([]);
  const { loading: leadTaskLoader, refetch: refetchLeadTask } = useQuery(GET_LEAD_TASKS, {
    variables: {
      leadId: +selectedLead.id,
      taskTypes: [],
      taskStatuses: selectedStatus,
      selectedOppTypeId: selectedOppTypeId || null,
    },
    skip: !selectedLead.id,
    fetchPolicy: "network-only",
    onCompleted: res => {
      dispatch(setTasks({ tasks: res.getLeadTasks.leadTasks, count: res.getLeadTasks.count }));
    },
  });
  const refetchTasks = async () => {
    const res = await refetchLeadTask();
    dispatch(setTasks(res.getLeadTasks));
  };
  return (
    <div className="flex-col-centered items-start gap-1">
      <div className="flex-row-centered gap-2">
        <span className="sm-400 text-grey-500">Filter By:</span>
        {/* <Dropdown menu={{ items: [] }}> */}
        <span className="sm-400 flex-row-centered gap-1 cursor-pointer text-brand-500">
          All Tasks
          {/* <DownIcon color="#3B82F6" /> */}
        </span>
        {/* </Dropdown> */}
      </div>
      {/* Status filters */}
      <div className="flex-row-centered gap-2">
        {taskStatus.map((item, index) => (
          <div
            className={`px-2 py-1 rounded-sm cursor-pointer xs-400 text-grey-700 ${
              selectedStatus.includes(item.value) ? "bg-grey-200" : "bg-grey-50"
            }`}
            key={index}
            onClick={() => {
              if (item.value === "ALL") {
                return setselectedStatus([]);
              }
              setselectedStatus([item.value]);
            }}
          >
            {capitalize(item.label)}
          </div>
        ))}
      </div>
      {/* Task Cards */}
      <div className="flex-col-centered gap-3 w-full">
        <Skeleton loading={leadTaskLoader} active avatar>
          {tasks && tasks.length > 0 ? (
            tasks
              .filter(item => {
                if (selectedStatus.length === 0) {
                  return item;
                }
                if (item.taskStatus === selectedStatus[0]) {
                  return item;
                }
              })
              .map((item, index) => (
                <TaskCard
                  item={item}
                  key={index}
                  setleadTaskData={setleadTaskData}
                  selectedLead={selectedLead}
                  refetchLeadTask={refetchTasks}
                />
              ))
          ) : (
            <div className="flex-row-centered justify-center w-full">
              <Empty description="Please add task for this lead" />
            </div>
          )}
        </Skeleton>
      </div>
    </div>
  );
};

const TaskCard = ({ item, setleadTaskData, selectedLead, refetchLeadTask }) => {
  const { getOpprDetails } = useCrm();
  const canDeleteTask = checkAccessPermission(AccessPermissionAction.TASK_MANAGEMENT_DELETE);
  const canEditTask = checkAccessPermission(AccessPermissionAction.TASK_MANAGEMENT_ADD_EDIT);

  const dispatch = useDispatch();
  const handleEdit = (isCompleted = false) => {
    setleadTaskData({
      open: true,
      mode: "EDIT",
      leadTask: item,
      selectedLead: selectedLead,
      isCompleted,
    });
  };

  const [updateLeadTask, { loading: updateLeadLoader }] = useMutation(UPDATE_LEAD_TASK);
  const [deleteLeadTask] = useMutation(DELETE_LEAD_TASK);
  const isTaskCompleted = record => record.taskStatus === "COMPLETED";
  const opportunityId = useGetOpportunityId(item?.lead?.leadOpportunities[0]);
  const CompletionTooltip = () => {
    return (
      <div className="flex-col-centered gap-2">
        <div className="flex-row-centered justify-between gap-3">
          <span>Completed on:</span>
          <span>{convertUtcToLocal(item.completionDate, "MMM DD, YYYY, hh:mm A")}</span>
        </div>
        <div className="flex-row-centered justify-between gap-3">
          <span>Completed note:</span>
          <span>{item?.completionNote || ""}</span>
        </div>
      </div>
    );
  };

  // Resolve this task's opp_type via its linked leadOpportunity. Tasks
  // without a leadOpportunityId render no chip — that matches notes /
  // opp cards which hide the chip when there's no root to display.
  const taskOpp = (item?.lead?.leadOpportunities || []).find(
    o => Number(o?.id) === Number(item?.leadOpportunityId)
  );
  const { label: opprLabel, fullLabel: opprFullLabel } = getOpprDetails({
    lead: item?.lead,
    leadOpportunityId: item.leadOpportunityId,
  });

  return (
    <div className="relative overflow-hidden flex-col-centered gap-4 rounded border border-solid border-grey-200">
      <TaskRootChip opportunityType={taskOpp?.opportunityType} />
      <div className="p-3 pb-0 flex-col-centered items-start">
        <Tooltip title={item.taskStatus === "COMPLETED" ? <CompletionTooltip /> : null}>
          <span
            className={`sm-600 text-brand-500 cursor-pointer ${
              item.taskStatus === "COMPLETED" && "line-through"
            }`}
            onClick={() => handleEdit()}
          >
            {item.title}
          </span>
        </Tooltip>
        <span className="xs-400 text-grey-500">
          {capitalize(snakeCaseToTitleCase(item.taskType))}{" "}
          <Tooltip title={opprFullLabel}>{opprLabel}</Tooltip>
        </span>
      </div>
      <div className="flex-row-centered px-3 pt-0 pb-2 justify-between items-end">
        <div className="flex-col-centered">
          <span className="text-xs text-grey-700">
            <b>Assigned to:</b> {item?.assignedTo?.fullName || ""}
          </span>
          {!isTaskCompleted(item) ? (
            <span className="xs-400 text-grey-700">
              Due on {formatDate(item.dueDate, "MMM DD, YYYY | hh:mm A")}
            </span>
          ) : (
            <span className="xs-400 text-grey-700">
              Completed on {formatDate(item.completionDate, "MMM DD, YYYY | hh:mm A")}
            </span>
          )}
        </div>
        <Dropdown
          menu={{
            items: [
              // { label: "View" },
              { label: "Edit", onClick: () => handleEdit(), disabled: !canEditTask },
              // { label: "Send Reminder", onClick: () => handleEdit() },
              ...(item.taskStatus === "COMPLETED"
                ? [
                    {
                      label: "Mark as open",
                      disabled: !canEditTask,
                      onClick: () => {
                        Modal.confirm({
                          title: "Are you sure you want to open this task again?",
                          onOk: async () => {
                            const res = await updateLeadTask({
                              variables: {
                                id: item.id,
                                taskStatus: "CREATED",
                              },
                            });
                            if (res.data.updateLeadTask.statusCode === 200) {
                              message.success("Lead task re-opened successfully!");
                              dispatch(reopenTask(item.id));
                            } else {
                              message.error(res.data.updateLeadTask.message);
                            }
                          },
                        });
                      },
                    },
                  ]
                : [
                    {
                      label: "Mark as completed",
                      disabled: !canEditTask,
                      onClick: () => handleEdit(true),
                    },
                  ]),
              {
                label: "Delete",
                danger: true,
                disabled: !canDeleteTask,
                onClick: () =>
                  Modal.confirm({
                    title: "Delete this task?",
                    icon: <ExclamationOutlined />,
                    content: "Are you sure you want to delete this task?",
                    onOk: async () => {
                      const res = await deleteLeadTask({
                        variables: {
                          taskIds: [+item.id],
                        },
                      });
                      if (res.data.deleteLeadTask.statusCode === 200) {
                        message.success("Task deleted successfully");
                        dispatch(deleteTask(item.id));
                      }
                    },
                    okButtonProps: {
                      loading: updateLeadLoader,
                    },
                    onCancel: () => {},
                  }),
              },
            ],
          }}
          placement="topLeft"
        >
          <div className="p-1 cursor-pointer rounded-sm border border-solid border-grey-200 flex-row-centered items-center justify-center">
            <MoreOutlined />
          </div>
        </Dropdown>
      </div>
    </div>
  );
};
